import { Component, OnDestroy, OnInit } from "@angular/core"
import { GTMService } from "../../services/gtm.service"
import { Resource } from "../../models/resource/resource"
import { CookiesService } from "../../services/cookies.service"
import { ResourceService } from "../../services/resource.service"
import { UserService } from "../../services/user.service"
import { CHNode } from "../../models/node.model"
import { SwiperOptions } from "swiper/types"
import { EduComponent } from "../../utils/eduComponent"
import { BrowsingService } from "../../services/browsing.service"
import {
    fadeAnimation,
    slideInOut,
} from "../../shared-components/vertical-menu/vertical-menu.animations"
import Utils from "../../utils/utils"
import { Title } from "@angular/platform-browser"
import { PBSLM_TITLE, STATIC_IMAGES } from "../../models/constants"
import { IWindowResizeEvent, WindowSizeService } from "../../services/window-size.service"
import { ActivatedRoute, Router } from "@angular/router"
import { NotificationsService } from "angular2-notifications"
import { ITSImage } from "../../models/itsImage.model"
import { Observable, Subject } from "rxjs"
import { takeUntil, tap } from "rxjs/operators"

const SUBJECTS_IN_GRID: number = 6

declare var window: any

@Component({
    selector: "app-student-homepage",
    templateUrl: "./student-homepage.component.html",
    styleUrls: ["./student-homepage.component.scss"],
    animations: [fadeAnimation, slideInOut],
    host: { class: "gtmpage-student-homepage" },
})
export class StudentHomepageComponent extends EduComponent implements OnInit, OnDestroy {
    private destroyed$: Subject<void> = new Subject<void>()
    code: string = ""
    slug: string
    resources: Resource[]
    heroImageUrl: string = ""
    remainingChildrenNr: number
    subjectTree: CHNode[]
    subjectTreeChildren: CHNode[] = []
    swiperOptions: SwiperOptions
    isDesktopResolution: boolean

    APISubjectsObservable$: Observable<CHNode[]> = this.browsingService.getSubjectsTree().pipe(
        takeUntil(this.destroyed$),
        tap(
            (event: CHNode[]) => this.getSubjects(event),
            (error) => console.error(`[CUSTOM Error]${error}`)
        )
    )

    constructor(
        private cookiesService: CookiesService,
        private resourceService: ResourceService,
        private userService: UserService,
        private gtmService: GTMService,
        private browsingService: BrowsingService,
        private titleService: Title,
        private windowSize: WindowSizeService,
        private router: Router,
        private notificationService: NotificationsService,
        private activatedRoute: ActivatedRoute
    ) {
        super()
        const parsedURL: URL = new URL(window.location.href)

        if (parsedURL.pathname.startsWith("/student")) {
            this.userService.setStudentExperience(true)
        }
    }

    ngOnInit(): void {
        this.initialiseHeroImages()
        if (this.shouldShowQuestion()) {
            this.resourceService.getRecommendationQuestion().subscribe((response: any) => {
                this.resources = response.results.map((resource) => new Resource(resource))
            })
        }
        this.titleService.setTitle(`Student Homepage | ${PBSLM_TITLE}`)
        this.gtmService.resetDataLayer()
        this.gtmService.pushPageView()
        this.APISubjectsObservable$.subscribe()
        this.isDesktopResolution = this.windowSize.isDesktop
        this.swiperOptions = StudentHomepageComponent.initialiseSwiperOptions(true)
        this.addSub(
            this.windowSize.resize.subscribe((windowResizeEvent: IWindowResizeEvent) => {
                this.isDesktopResolution = windowResizeEvent.isDesktop
            })
        )
    }

    shouldShowQuestion(): boolean {
        return (
            !!window.PBSLM.FEATURE_FLAGS.RECOMMENDATION_ENGINE_INTEREST_QUIZ &&
            !this.cookiesService.getCookie("interest_quiz_completed")
        )
    }

    voteResource(resIndex: 0 | 1) {
        const otherResIndex = resIndex === 0 ? 1 : 0
        this.resourceService.rateResource(this.resources[resIndex].resourceCode, 5)
        this.resourceService.rateResource(this.resources[otherResIndex].resourceCode, 2)
        this.cookiesService.setCookie("interest_quiz_completed", "1", 7)
    }

    onSelectedSubject({ subject, isSlideChangeEvent }) {
        /**
         * For any resolution under desktop,
         * clicking the subject opens that subject’s browse page
         * instead of opening the subject bubble menu (subject clouds)
         */
        if (!this.windowSize.isDesktop && !isSlideChangeEvent) {
            const params = {}
            if (this.userService.hasStudentExperience()) params["student"] = true
            return this.router.navigate([`/subjects/${subject.slug}`], { queryParams: params })
        }

        this.slug = subject.slug
        this.remainingChildrenNr = 0

        const children = subject.children.map((child) => {
            const { posterImageAltText, posterImage, slug, title } = child
            return {
                posterImageAltText,
                posterImage,
                slug,
                title,
            }
        })

        if (children.length < SUBJECTS_IN_GRID) {
            return (this.subjectTreeChildren = [...children])
        }

        this.subjectTreeChildren = Utils.getRandomItems(children, SUBJECTS_IN_GRID)
        this.remainingChildrenNr = children.length - SUBJECTS_IN_GRID
    }

    public enterCode(event) {
        event.preventDefault()
        if (!this.code) {
            this.notificationService.error("", "Please enter a code", Utils.alertConfiguration)
            return
        }

        this.userService.enterStudentCode(this.code).subscribe(
            (data) => {
                if (data.error) {
                    return this.notificationService.error("", data.error, Utils.alertConfiguration)
                }
                if (data.url) this.router.navigate([data.url])
            },
            () => {
                this.notificationService.error(
                    "Uh oh!",
                    "Invalid code entered.",
                    Utils.alertConfiguration
                )
            }
        )
    }

    private getSubjects(data: CHNode[] | any) {
        this.subjectTree = data
    }

    private initialiseHeroImages(): void {
        const { heroImages, error } = this.activatedRoute.snapshot.data.heroImages

        // if the api throws and error we only log it, as we have an image fallback
        if (error) console.error(`[CUSTOM Error]${error.message}`)

        const heroImage: ITSImage = StudentHomepageComponent.getHeroImage(heroImages)
        this.heroImageUrl = `url(${heroImage.itsURL})`
    }

    private static initialiseSwiperOptions(navigation = true): SwiperOptions {
        return {
            autoplay: {
                delay: 5000,
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
            },
            coverflowEffect: {
                rotate: 0,
                depth: 0,
                modifier: 0,
                slideShadows: false,
            },
            spaceBetween: 15,
            slidesPerView: 5,
            ...(navigation === true && { navigation }),
        }
    }

    /**
     * Get a background hero image, filtered and with a fallback value
     * @param heroImages
     * @private
     */
    private static getHeroImage(heroImages: ITSImage[] | []): ITSImage {
        // filter out images without url
        const validImages: ITSImage[] = heroImages.length
            ? heroImages.filter((image: ITSImage) => image && image.itsURL)
            : []

        // if no hero image is set from admin, provide a fallback image
        if (Utils.isEmpty(validImages)) {
            return new ITSImage(STATIC_IMAGES.default_student_hero, "Student Hero Image")
        }

        // return one random image object for student hero
        return Utils.getRandomItems(validImages, 1)[0]
    }

    ngOnDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
